import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MobileContextService, ContextoPWA } from '@core/context/services/mobile-context.service';
import { ContextApiService } from '@core/context/services/context-api.service';
import { ProfileService } from '@core/context/services/profile.service';
import { ItemEducacionalColorService } from '@core/context/services/item-educacional-color.service';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import {
  AnoLetivoOption,
  ComponenteOption,
  EntidadeOption,
  EstabelecimentoOption,
  ServidorOption,
  TurmaOption
} from './context-selector-modal.model';
import { filtrarLista, normalizarTexto } from '@shared/list-search.util';
import {
  buildContextoPWA,
  construirHeadersContextuais,
  encontrarServidorNoFallback,
  HeadersContextuais,
  mapearAnosLetivos,
  mapearComponentes,
  mapearEntidadesUnicas,
  mapearEstabelecimentos,
  mapearServidores,
  mapearTurmas,
  selecionarAnoLetivoRestaurado,
  selecionarComponentesRestaurados,
  selecionarEntidadeRestaurada,
  selecionarEstabelecimentoRestaurado,
  selecionarServidorRestaurado,
  selecionarTurmaRestaurada
} from './context-selector-modal.utils';

@Component({
  selector: 'app-context-selector-modal',
  templateUrl: './context-selector-modal.component.html',
  styleUrls: ['./context-selector-modal.component.scss']
})
export class ContextSelectorModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() canClose = true;
  @Output() fechado = new EventEmitter<void>();
  @Output() contextChanged = new EventEmitter<ContextoPWA>();

  entidades: EntidadeOption[] = [];
  servidores: ServidorOption[] = [];
  anosLetivos: AnoLetivoOption[] = [];
  estabelecimentos: EstabelecimentoOption[] = [];
  turmas: TurmaOption[] = [];
  componentes: ComponenteOption[] = [];

  entidadeSelecionada: number | null = null;
  entidadeSelecionadaData: EntidadeOption | null = null; // Guarda o licenseId
  servidorSelecionado: number | null = null;
  anoLetivoSelecionado: number | null = null;
  estabelecimentoSelecionado: number | null = null;
  turmaSelecionada: number | null = null;

  // Hierarquia progressiva: cada campo habilita após selecionar o anterior
  camposHabilitados = {
    entidade: true,
    servidor: false,
    anoLetivo: false,
    estabelecimento: false,
    turma: false,
    componentes: false
  };

  carregando = false;
  erro: string | null = null;

  loadingStates = {
    entidades: false,
    servidores: false,
    anosLetivos: false,
    estabelecimentos: false,
    turmas: false,
    componentes: false
  };

  isTurmasEmpty = false;

  /** Quando a lista da API vem vazia: rótulo auxiliar + texto do botão (evita só “Selecione...”). */
  readonly msgSemOpcoes = {
    entidades: 'Não há entidades disponíveis',
    servidores: 'Não há servidores disponíveis',
    anoLetivo: 'Não há ano letivo disponível',
    estabelecimentos: 'Não há estabelecimentos disponíveis',
    turmas: 'Não há turmas disponíveis',
    componentes: 'Não há componentes curriculares disponíveis',
  } as const;

  // Professor com entidade única: campo bloqueado
  entidadeDesabilitada = false;

  // Servidor vinculado automaticamente ao professor: campo bloqueado
  servidorProfessorVinculado = false;

  // Chamada de API em andamento para identificar se é professor
  detectandoServidorProfessor = false;

  entidadeDropdownAberto = false;
  servidorDropdownAberto = false;
  anoLetivoDropdownAberto = false;
  estabelecimentoDropdownAberto = false;
  turmaDropdownAberta = false;

  // Termos de busca (componentes não tem)
  entidadeSearchTerm = '';
  servidorSearchTerm = '';
  anoLetivoSearchTerm = '';
  estabelecimentoSearchTerm = '';
  turmaSearchTerm = '';

  entidadesFiltradas: EntidadeOption[] = [];
  servidoresFiltrados: ServidorOption[] = [];
  anosLetivosFiltrados: AnoLetivoOption[] = [];
  estabelecimentosFiltrados: EstabelecimentoOption[] = [];
  turmasFiltradas: TurmaOption[] = [];

  private jaCarregouContexto = false;
  private emProcessoDeRestauracao = false;

  private servidorSearchSubject = new Subject<string>();
  private servidorBuscaSubscription: Subscription | null = null;

  private contextoAnterior: ContextoPWA | null = null;

  // Accessors passados ao <app-hierarquia-select> (declarados como propriedades — não
  // recriar arrow functions no template a cada ciclo de change detection).
  readonly getOptionId = (opt: { id: number | null }): number | null => opt.id;
  readonly getEntidadeLabel = (e: EntidadeOption): string => e.nome;
  readonly getServidorLabel = (s: ServidorOption): string => s.nome;
  readonly getAnoLetivoLabel = (a: AnoLetivoOption): string =>
    a.descricao ? `${a.ano} - ${a.descricao}` : a.ano.toString();
  readonly getEstabelecimentoLabel = (e: EstabelecimentoOption): string => e.nome;
  readonly getTurmaLabel = (t: TurmaOption): string => t.descricao || t.nome || 'Turma sem descrição';

  constructor(
    private contextService: MobileContextService,
    private contextApiService: ContextApiService,
    private profileService: ProfileService,
    private colorService: ItemEducacionalColorService
  ) {}

  ngOnInit() {
    // Carregamento fica em ngOnChanges — evita dupla execução
    this.inicializarBuscaServidorDebounced();
  }

  ngOnDestroy(): void {
    this.servidorBuscaSubscription?.unsubscribe();
    this.servidorSearchSubject.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue === true) {
        this.jaCarregouContexto = false;
        this.carregarContextoAtual();
      } else {
        this.jaCarregouContexto = false;
      }
    }
  }

  private carregarContextoAtual(): void {
    if (this.jaCarregouContexto) {
      return;
    }

    this.jaCarregouContexto = true;

    try {
      this.erro = null;

      const contextoSalvo = this.contextService.getContextoAtual();

      // Guarda para possível restauração no cancelamento
      this.contextoAnterior = contextoSalvo ? { ...contextoSalvo } : null;

      if (contextoSalvo && contextoSalvo.turma?.id) {
        this.restaurarContextoCompleto(contextoSalvo);
      } else {
        this.inicializarModalVazio();
      }

    } catch (e) {
      this.erro = 'Erro ao carregar contexto atual';
      this.inicializarModalVazio();
    }
  }

  private inicializarModalVazio(): void {
    this.entidades = [];
    this.servidores = [];
    this.anosLetivos = [];
    this.estabelecimentos = [];
    this.turmas = [];
    this.componentes = [];

    this.entidadeSelecionada = null;
    this.entidadeSelecionadaData = null;
    this.servidorSelecionado = null;
    this.anoLetivoSelecionado = null;
    this.estabelecimentoSelecionado = null;
    this.turmaSelecionada = null;

    this.entidadesFiltradas = [];
    this.servidoresFiltrados = [];
    this.anosLetivosFiltrados = [];
    this.estabelecimentosFiltrados = [];
    this.turmasFiltradas = [];

    this.entidadeSearchTerm = '';
    this.servidorSearchTerm = '';
    this.anoLetivoSearchTerm = '';
    this.estabelecimentoSearchTerm = '';
    this.turmaSearchTerm = '';

    this.camposHabilitados = {
      entidade: true,
      servidor: false,
      anoLetivo: false,
      estabelecimento: false,
      turma: false,
      componentes: false
    };

    this.entidadeDesabilitada = false;
    this.servidorProfessorVinculado = false;
    this.detectandoServidorProfessor = false;

    this.carregarEntidades();
  }

  /**
   * Restaura contexto em cascata, pré-selecionando cada nível progressivamente.
   * Só o nível Entidade é crítico: se falhar, aborta e reinicia a modal vazia.
   * Os demais níveis toleram falha e mantêm os anteriores já restaurados.
   */
  private async restaurarContextoCompleto(contexto: ContextoPWA): Promise<void> {
    this.emProcessoDeRestauracao = true;

    const resultados = {
      entidade: false,
      servidor: false,
      anoLetivo: false,
      estabelecimento: false,
      turma: false,
      componentes: false
    };

    try {
      await this.restaurarEntidade(contexto);
      resultados.entidade = true;
    } catch (error) {
      this.inicializarModalVazio();
      this.emProcessoDeRestauracao = false;
      return;
    }

    if (contexto.servidor?.id) {
      try {
        await this.restaurarServidor(contexto);
        resultados.servidor = true;
      } catch (error) {
        // Não crítico — segue sem servidor restaurado
      }
    }

    try {
      await this.restaurarAnoLetivo(contexto);
      resultados.anoLetivo = true;
    } catch (error) {
      // não crítico
    }

    try {
      await this.restaurarEstabelecimento(contexto);
      resultados.estabelecimento = true;
    } catch (error) {
      // não crítico
    }

    try {
      await this.restaurarTurma(contexto);
      resultados.turma = true;
    } catch (error) {
      // não crítico
    }

    // Carrega mesmo sem componentes salvos no contexto — nesse caso seleciona todos por padrão
    if (this.turmaSelecionada) {
      try {
        await this.restaurarComponentes(contexto);
        resultados.componentes = true;
      } catch (error) {
        // não crítico
      }
    }
    this.entidadeDesabilitada = this.entidades.length === 1;

    this.emProcessoDeRestauracao = false;
  }

  private restaurarEntidade(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingStates.entidades = true;
      
      this.contextApiService.listEntidades().subscribe({
        next: (entidades) => {
          this.entidades = mapearEntidadesUnicas(entidades);
          this.filtrarEntidades();
          this.loadingStates.entidades = false;

          const entidadeParaSelecionar = selecionarEntidadeRestaurada(this.entidades, contexto);

          if (entidadeParaSelecionar) {
            this.entidadeSelecionada = entidadeParaSelecionar.id;
            this.entidadeSelecionadaData = entidadeParaSelecionar;
            this.camposHabilitados.servidor = true;
            resolve();
          } else {
            reject(new Error('Entidade não encontrada'));
          }
        },
        error: (err) => {
          this.loadingStates.entidades = false;
          // O interceptor já chama @me/access sem User-Access (evita 504), então
          // falha aqui é erro real de sessão — sempre propaga.
          reject(err);
        }
      });
    });
  }

  private restaurarServidor(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve) => {
      this.loadingStates.servidores = true;

      if (!this.entidadeSelecionadaData?.id || !this.entidadeSelecionadaData.licenseId) {
        this.loadingStates.servidores = false;
        resolve();
        return;
      }

      this.contextApiService.getServidorDefaultProfessor(
        this.entidadeSelecionadaData.id,
        this.entidadeSelecionadaData.licenseId
      ).subscribe({
        next: (servidorProfessor) => {
          if (servidorProfessor?.id) {
            // Professor: servidor vinculado — preenche e bloqueia campo
            this.servidores = [{ id: servidorProfessor.id, nome: servidorProfessor.pessoa.nome }];
            this.servidoresFiltrados = [...this.servidores];
            this.servidorSelecionado = servidorProfessor.id;
            this.servidorProfessorVinculado = true;
            this.camposHabilitados.servidor = true;
            this.camposHabilitados.anoLetivo = true;
            this.profileService.setAsProfessor();
            this.loadingStates.servidores = false;
            resolve();
          } else {
            // Secretário: usa endpoint genérico
            this.servidorProfessorVinculado = false;
            this.carregarServidoresParaRestauracao(contexto, resolve);
          }
        },
        error: () => {
          this.servidorProfessorVinculado = false;
          this.carregarServidoresParaRestauracao(contexto, resolve);
        }
      });
    });
  }

  private carregarServidoresParaRestauracao(contexto: ContextoPWA, resolve: () => void): void {
    const headersContextuais = this.construirHeadersContextuais();
    const userAccessId = headersContextuais.userAccessId;
    const params: any = { filter: '', limit: 100, offset: 0, userAccessId };

    this.contextApiService.getServidores(params).subscribe({
      next: (servidores) => {
        this.servidores = mapearServidores(servidores);
        this.servidorSearchTerm = '';
        this.filtrarServidores();
        this.loadingStates.servidores = false;

        const servidorNome = contexto.servidor?.pessoa?.nome;
        const servidorParaSelecionar = selecionarServidorRestaurado(this.servidores, contexto);

        if (servidorParaSelecionar) {
          this.aplicarServidorRestaurado(servidorParaSelecionar);
          resolve();
        } else if (servidorNome) {
          this.contextApiService.getServidores({ filter: servidorNome, limit: 10, offset: 0, userAccessId }).subscribe({
            next: (fallback) => {
              const encontrado = encontrarServidorNoFallback(fallback, contexto.servidor?.id, servidorNome);
              if (encontrado) {
                this.servidores = [...this.servidores, encontrado];
                this.filtrarServidores();
                this.aplicarServidorRestaurado(encontrado);
              }
              resolve();
            },
            error: () => resolve()
          });
        } else {
          resolve();
        }
      },
      error: () => {
        this.loadingStates.servidores = false;
        resolve();
      }
    });
  }

  private aplicarServidorRestaurado(servidor: ServidorOption): void {
    this.servidorSelecionado = servidor.id;
    this.camposHabilitados.servidor = true;
    this.camposHabilitados.anoLetivo = true;
    const profile = this.profileService.getProfileDefault({ id: servidor.id });
    if (profile) {
      this.profileService.setProfile(profile);
    }
  }

  private restaurarAnoLetivo(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingStates.anosLetivos = true;
      
      const headersContextuais = this.construirHeadersContextuais();
      const params: any = {
        userAccessId: headersContextuais.userAccessId,
        servidorId: this.servidorSelecionado
      };

      this.contextApiService.getAnosLetivos(params).subscribe({
        next: (anos) => {
          this.anosLetivos = mapearAnosLetivos(anos);
          this.filtrarAnosLetivos();
          this.loadingStates.anosLetivos = false;

          // Pré-selecionar ano letivo - buscar por ID ou ano
          const anoSelecionado = selecionarAnoLetivoRestaurado(this.anosLetivos, contexto);
          if (anoSelecionado != null) {
            this.anoLetivoSelecionado = anoSelecionado;
            this.camposHabilitados.estabelecimento = true;
            resolve();
          } else {
            reject(new Error('Ano Letivo não encontrado'));
          }
        },
        error: (err) => {
          this.loadingStates.anosLetivos = false;
          reject(err);
        }
      });
    });
  }

  private restaurarEstabelecimento(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingStates.estabelecimentos = true;
      
      const headersContextuais = this.construirHeadersContextuais();
      const params: any = { 
        userAccessId: headersContextuais.userAccessId,
        servidorId: this.servidorSelecionado,
        anoLetivoId: this.anoLetivoSelecionado
      };
      
      this.contextApiService.getEstabelecimentos(params).subscribe({
        next: (estabelecimentos) => {
          this.estabelecimentos = mapearEstabelecimentos(estabelecimentos);
          this.filtrarEstabelecimentos();
          this.loadingStates.estabelecimentos = false;

          // Pré-selecionar estabelecimento - buscar por ID ou nome
          const estabelecimentoSelecionadoId = selecionarEstabelecimentoRestaurado(this.estabelecimentos, contexto);
          if (estabelecimentoSelecionadoId != null) {
            this.estabelecimentoSelecionado = estabelecimentoSelecionadoId;
            this.camposHabilitados.turma = true;
            resolve();
          } else {
            reject(new Error('Estabelecimento não encontrado'));
          }
        },
        error: (err) => {
          this.loadingStates.estabelecimentos = false;
          reject(err);
        }
      });
    });
  }

  private restaurarTurma(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loadingStates.turmas = true;
      
      const headersContextuais = this.construirHeadersContextuais();
      const params: any = { 
        filter: '', 
        offset: 0, 
        limit: 100,
        userAccessId: headersContextuais.userAccessId,
        servidorId: this.servidorSelecionado,
        anoLetivoId: this.anoLetivoSelecionado,
        estabelecimentoId: this.estabelecimentoSelecionado
      };
      
      this.contextApiService.getTurmas(params).subscribe({
        next: (turmas) => {
          this.turmas = mapearTurmas(turmas.content);
          this.filtrarTurmas();
          this.loadingStates.turmas = false;
          this.isTurmasEmpty = this.turmas.length === 0;

          // Pré-selecionar turma - buscar por ID ou descrição
          const turmaSelecionadaId = selecionarTurmaRestaurada(this.turmas, contexto);
          if (turmaSelecionadaId != null) {
            this.turmaSelecionada = turmaSelecionadaId;
            this.camposHabilitados.componentes = true;
            resolve();
          } else {
            reject(new Error('Turma não encontrada'));
          }
        },
        error: (err) => {
          this.loadingStates.turmas = false;
          reject(err);
        }
      });
    });
  }

  private restaurarComponentes(contexto: ContextoPWA): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.turmaSelecionada) {
        resolve();
        return;
      }

      this.loadingStates.componentes = true;
      
      const headersContextuais = this.construirHeadersContextuais();
      const { userAccessId, contexto: contextoHeaders } = headersContextuais;
      const { servidor: servidorId, anoLetivo: anoLetivoId, estabelecimento: estabelecimentoId } = contextoHeaders;
      
      const params = {
        userAccessId,
        servidorId,
        anoLetivoId,
        estabelecimentoId
      };
      
      this.contextApiService.getComponentesCurriculares(this.turmaSelecionada, params).subscribe({
        next: (componentes) => {
          const componentesMapeados = mapearComponentes(componentes);
          // Pré-selecionar componentes (contexto salvo, ou seleção única/todos como fallback)
          this.componentes = selecionarComponentesRestaurados(
            componentesMapeados,
            contexto,
            this.isSelecaoUnicaComponentes()
          );
          this.loadingStates.componentes = false;

          resolve();
        },
        error: (err) => {
          this.loadingStates.componentes = false;
          // Não rejeita - componentes podem ser opcionais
          resolve();
        }
      });
    });
  }

  private carregarEntidades(): void {
    this.loadingStates.entidades = true;
    this.erro = null;

    this.contextApiService.listEntidades().subscribe({
      next: (entidades) => {
        // Agrupa por entityId: usuário com múltiplos acessos à mesma entidade a vê só uma vez
        const entidadesUnicas = new Map<number, any>();

        entidades.forEach(e => {
          const entityId = Number(e.id);
          if (!entidadesUnicas.has(entityId)) {
            entidadesUnicas.set(entityId, {
              id: e.id, 
              nome: e.nome || 'Entidade sem nome',
              licenseId: e.licenseId,
              access: e.access,
              admin: e.admin
            });
          }
        });
        
        this.entidades = Array.from(entidadesUnicas.values());
        this.loadingStates.entidades = false;
        this.filtrarEntidades();

        // Auto-seleciona e bloqueia quando há apenas 1 entidade disponível
        if (this.entidades.length === 1) {
          this.entidadeDesabilitada = true;
          this.selecionarEntidade(this.entidades[0]);
        }
      },
      error: (err) => {
        this.erro = 'Não foi possível carregar as entidades. Tente novamente.';
        this.loadingStates.entidades = false;
      }
    });
  }

  // ============================================================
  // MÉTODOS DE LIMPEZA EM CASCATA
  // ============================================================

  /**
   * Cascata ordenada servidor→...→componentes. Cada `limpar()` reproduz exatamente
   * o corpo do método `limparXEAbaixo` equivalente (sem chamar o próximo nível —
   * quem encadeia é `limparNivelEAbaixo`, iterando a partir do nível pedido).
   */
  private readonly niveisCascata: Array<{ chave: string; limpar: () => void }> = [
    {
      chave: 'servidor',
      limpar: () => {
        this.servidorSelecionado = null;
        this.servidores = [];
        this.camposHabilitados.servidor = false;
      }
    },
    {
      chave: 'anoLetivo',
      limpar: () => {
        this.anoLetivoSelecionado = null;
        this.anosLetivos = [];
        this.camposHabilitados.anoLetivo = false;
      }
    },
    {
      chave: 'estabelecimento',
      limpar: () => {
        this.estabelecimentoSelecionado = null;
        this.estabelecimentos = [];
        this.camposHabilitados.estabelecimento = false;
      }
    },
    {
      chave: 'turma',
      limpar: () => {
        this.turmaSelecionada = null;
        this.turmas = [];
        this.camposHabilitados.turma = false;
      }
    },
    {
      chave: 'componentes',
      limpar: () => {
        this.componentes = [];
        this.camposHabilitados.componentes = false;
      }
    }
  ];

  private limparNivelEAbaixo(chave: string): void {
    const indiceInicial = this.niveisCascata.findIndex(n => n.chave === chave);
    if (indiceInicial === -1) return;
    for (let i = indiceInicial; i < this.niveisCascata.length; i++) {
      this.niveisCascata[i].limpar();
    }
  }

  private limparServidorEAbaixo(): void {
    this.limparNivelEAbaixo('servidor');
  }

  private limparAnoLetivoEAbaixo(): void {
    this.limparNivelEAbaixo('anoLetivo');
  }

  private limparEstabelecimentoEAbaixo(): void {
    this.limparNivelEAbaixo('estabelecimento');
  }

  private limparTurmaEAbaixo(): void {
    this.limparNivelEAbaixo('turma');
  }

  private limparComponentes(): void {
    this.limparNivelEAbaixo('componentes');
  }

  // ============================================================
  // MÉTODOS DE CARREGAMENTO FILTRADO
  // ============================================================

  /**
   * Constrói headers HTTP contextuais baseados nas seleções ATUAIS do modal.
   * Isso garante que a API receba o contexto NOVO (recém-selecionado)
   * em vez do contexto ANTIGO (que ainda está no localStorage).
   */
  private construirHeadersContextuais(): HeadersContextuais {
    return construirHeadersContextuais(
      this.entidadeSelecionadaData,
      this.servidorSelecionado,
      this.anoLetivoSelecionado,
      this.estabelecimentoSelecionado,
      this.turmaSelecionada
    );
  }

  // User-Access header garante filtragem correta no backend
  private carregarServidoresFiltrados(): void {
    if (!this.entidadeSelecionada) {
      this.servidores = [];
      return;
    }

    // Limpa lista e seleção antes de carregar, exceto durante restauração (perderia os valores)
    if (!this.emProcessoDeRestauracao) {
      this.servidores = [];
      this.servidorSelecionado = null;
    }

    this.loadingStates.servidores = true;
    this.erro = null;

    const headersContextuais = this.construirHeadersContextuais();
    const params: any = { 
      filter: '', 
      limit: 25, 
      offset: 0,
      userAccessId: headersContextuais.userAccessId
    };

    this.contextApiService.getServidores(params).subscribe({
      next: (servidores) => {
        this.servidores = servidores.map(s => ({
          id: s.id,
          nome: s.pessoa.nome
        }));

        this.loadingStates.servidores = false;
        this.filtrarServidores();
      },
      error: (err) => {
        // Retry automático em 401
        if (err.status === 401) {
            setTimeout(() => {
                this.contextApiService.getServidores(params).subscribe({
                    next: (servidoresRetry) => {
                        this.servidores = servidoresRetry.map(s => ({
                            id: s.id,
                            nome: s.pessoa.nome
                        }));
                        this.loadingStates.servidores = false;
                        this.filtrarServidores();
                        this.erro = null;
                    },
                    error: (errRetry) => {
                        this.erro = 'Não foi possível autenticar. Tente recarregar a página.';
                        this.loadingStates.servidores = false;
                    }
                });
            }, 1500);
            return;
        }
        this.erro = 'Não foi possível carregar a lista de servidores. Tente novamente.';
        this.loadingStates.servidores = false;
        this.servidores = [];
      }
    });
  }

  /**
   * Detecta se o usuário é professor (servidor vinculado, campo bloqueado) ou
   * secretário (dropdown manual). Replica $contextoServidor.getServidorDefault() do AngularJS.
   */
  private detectarServidorProfessor(entidade: EntidadeOption): void {
    if (!entidade.id || !entidade.licenseId) {
      this.servidorProfessorVinculado = false;
      if (!this.emProcessoDeRestauracao) {
        this.camposHabilitados.servidor = true;
        this.carregarServidoresFiltrados();
      }
      return;
    }

    this.detectandoServidorProfessor = true;
    this.loadingStates.servidores = true;
    this.erro = null;

    this.contextApiService.getServidorDefaultProfessor(entidade.id, entidade.licenseId).subscribe({
      next: (servidor) => {
        this.detectandoServidorProfessor = false;
        if (servidor?.id) {
          this.servidorProfessorVinculado = true;
          this.profileService.setAsProfessor();
          this.camposHabilitados.servidor = true;

          if (!this.emProcessoDeRestauracao) {
            this.servidores = [{ id: servidor.id, nome: servidor.pessoa.nome }];
            this.servidoresFiltrados = [...this.servidores];
            this.loadingStates.servidores = false;
            this.selecionarServidor(this.servidores[0]);
          } else {
            this.loadingStates.servidores = false;
          }
        } else {
          this.servidorProfessorVinculado = false;
          this.loadingStates.servidores = false;
          if (!this.emProcessoDeRestauracao) {
            this.camposHabilitados.servidor = true;
            this.carregarServidoresFiltrados();
          }
        }
      },
      error: () => {
        this.detectandoServidorProfessor = false;
        this.servidorProfessorVinculado = false;
        this.loadingStates.servidores = false;
        if (!this.emProcessoDeRestauracao) {
          this.camposHabilitados.servidor = true;
          this.carregarServidoresFiltrados();
        }
      }
    });
  }

  // Usa headers contextuais com entidade e servidor recém-selecionados
  private carregarAnosLetivosFiltrados(): void {
    if (!this.servidorSelecionado) {
      this.anosLetivos = [];
      return;
    }
    this.anosLetivos = [];
    this.anoLetivoSelecionado = null;
    this.loadingStates.anosLetivos = true;
    this.erro = null;

    const headersContextuais = this.construirHeadersContextuais();
    const params = {
      ...headersContextuais,
      servidorId: this.servidorSelecionado
    };

    this.contextApiService.getAnosLetivos(params).subscribe({
      next: (anos) => {
        this.anosLetivos = mapearAnosLetivos(anos);
        this.loadingStates.anosLetivos = false;
        this.filtrarAnosLetivos();
      },
      error: (err) => {
        this.erro = 'Não foi possível carregar os anos letivos. Tente novamente.';
        this.loadingStates.anosLetivos = false;
      }
    });
  }

  // User-Access header garante que só retornam estabelecimentos da entidade
  private carregarEstabelecimentosFiltrados(): void {
    if (!this.anoLetivoSelecionado) {
      this.estabelecimentos = [];
      return;
    }

    if (!this.emProcessoDeRestauracao) {
      this.estabelecimentos = [];
      this.estabelecimentoSelecionado = null;
    }

    this.loadingStates.estabelecimentos = true;
    this.erro = null;

    const headersContextuais = this.construirHeadersContextuais();
    const params = {
      ...headersContextuais,
      servidorId: this.servidorSelecionado ?? undefined,
      anoLetivoId: this.anoLetivoSelecionado ?? undefined
    };
    
    this.contextApiService.getEstabelecimentos(params).subscribe({
      next: (estabelecimentos) => {
        this.estabelecimentos = estabelecimentos.map(e => ({
          id: e.id,
          nome: e.descricao
        }));
        this.loadingStates.estabelecimentos = false;
        this.filtrarEstabelecimentos();
      },
      error: (err) => {
        this.erro = 'Não foi possível carregar os estabelecimentos. Tente novamente.';
        this.loadingStates.estabelecimentos = false;
      }
    });
  }

  private carregarTurmasFiltradas(): void {
    if (!this.estabelecimentoSelecionado) {
      this.turmas = [];
      return;
    }

    if (!this.emProcessoDeRestauracao) {
      this.turmas = [];
      this.turmaSelecionada = null;
      this.isTurmasEmpty = false;
    }

    this.loadingStates.turmas = true;
    this.erro = null;

    const headersContextuais = this.construirHeadersContextuais();
    const params = {
      filter: '',
      limit: 100,
      offset: 0,
      userAccessId: headersContextuais.userAccessId,
      servidorId: this.servidorSelecionado ?? undefined,
      anoLetivoId: this.anoLetivoSelecionado ?? undefined,
      estabelecimentoId: this.estabelecimentoSelecionado ?? undefined
    };
    
    this.contextApiService.getTurmas(params).subscribe({
      next: (response) => {
        // Filtro no frontend — a API retorna todas as turmas do servidor
        const turmasFiltradas = response.content.filter(t =>
          t.estabelecimento?.id === this.estabelecimentoSelecionado && !t.etapaMatriz?.modular
        );
        
        this.turmas = turmasFiltradas.map(t => ({
          id: t.id,
          descricao: t.descricao,
          nome: t.nome,
          tipoAtendimento: t.tipoAtendimento,
          modalidade: (t as any).modalidade,
          etapaMatriz: (t as any).etapaMatriz ?? null
        }));

        this.loadingStates.turmas = false;
        this.isTurmasEmpty = this.turmas.length === 0;
        this.filtrarTurmas();
      },
      error: (err) => {
        this.erro = 'Não foi possível carregar as turmas. Tente novamente.';
        this.loadingStates.turmas = false;
      }
    });
  }

  private carregarComponentes(turmaId: number): void {
    this.componentes = [];
    this.loadingStates.componentes = true;
    this.erro = null;

    // Turma vai só na URL, não no app-context
    const params = {
      userAccessId: this.entidadeSelecionadaData?.licenseId,
      servidorId: this.servidorSelecionado ?? undefined,
      anoLetivoId: this.anoLetivoSelecionado ?? undefined,
      estabelecimentoId: this.estabelecimentoSelecionado ?? undefined
    };
    
    this.contextApiService.getComponentesCurriculares(turmaId, params).subscribe({
      next: (componentes) => {
        this.componentes = componentes.map(c => ({
          id: c.id,
          descricao: c.descricao || c.nome || '',
          nome: c.nome,
          isCampoExperiencia: c.isCampoExperiencia,
          selected: false // usuário deve escolher explicitamente
        }));
        this.loadingStates.componentes = false;

        if (this.componentes.length === 0) {
          this.erro = 'Esta turma não possui componentes curriculares cadastrados.';
        }
      },
      error: (err) => {
        this.erro = 'Não foi possível carregar os componentes curriculares. Tente novamente.';
        this.loadingStates.componentes = false;
      }
    });
  }

  isTurmaSelecionadaAtividade(): boolean {
    if (!this.turmaSelecionada) return false;
    const turma = this.turmas.find(t => t.id === this.turmaSelecionada);
    return turma?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR' ||
           turma?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO';
  }

  isTurmaSelecionadaEjaModular(): boolean {
    if (!this.turmaSelecionada) return false;
    const turma = this.turmas.find(t => t.id === this.turmaSelecionada) as any;
    return !!(turma?.etapaMatriz?.modular || turma?.modalidade === 'EJA_MODULAR');
  }

  /** Quando true, o bloco de componentes deve funcionar como seleção única. */
  isSelecaoUnicaComponentes(): boolean {
    return this.isTurmaSelecionadaAtividade() || this.isTurmaSelecionadaEjaModular();
  }

  getComponentesLength(): number {
    return this.componentes.filter(c => c.selected).length;
  }

  async aplicar(): Promise<void> {
    if (!this.turmaSelecionada) {
      this.erro = 'Selecione uma turma';
      return;
    }

    const componentesSelecionados = this.componentes.filter(c => c.selected);
    if (componentesSelecionados.length === 0) {
      this.erro = 'Selecione pelo menos um componente curricular';
      return;
    }

    // Ordem dos componentes deve refletir o array original (API)
    const novoContexto: ContextoPWA = buildContextoPWA(
      this.entidadeSelecionada,
      this.entidades,
      this.servidorSelecionado,
      this.servidores,
      this.anoLetivoSelecionado,
      this.anosLetivos,
      this.estabelecimentoSelecionado,
      this.estabelecimentos,
      this.turmaSelecionada,
      this.turmas,
      this.componentes
    );

    // Persiste no localStorage: sem AngularJS, a bridge não popula User-Access/app-context
    // sozinha, e o interceptor HTTP depende deles no modo standalone/TWA
    this.contextService.persistBridgeAccess(
      this.entidadeSelecionadaData,
      this.servidorSelecionado,
      this.anoLetivoSelecionado,
      this.estabelecimentoSelecionado
    );

    // Aguarda para garantir que o contexto foi salvo antes de fechar a modal
    await this.contextService.setContext(novoContexto);

    this.fechar(true);
  }

  fechar(force = false): void {
    if (!this.canClose && !force) return;
    this.erro = null;
    this.fechado.emit();
  }

  /** Sem contexto anterior, apenas fecha — a tela de frequência trata a ausência de contexto. */
  cancelar(): void {
    if (this.contextoAnterior && this.contextoAnterior.turma?.id) {
      this.contextService.setContext(this.contextoAnterior);
    }
    this.fechar(true);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.fechar();
    }
  }

  // Métodos para controlar dropdowns customizados
  /** Um accessor por dropdown — fecha todos os outros ao abrir o pedido. */
  private readonly dropdownFlags: Array<{ chave: string; get: () => boolean; set: (v: boolean) => void }> = [
    { chave: 'entidade', get: () => this.entidadeDropdownAberto, set: v => this.entidadeDropdownAberto = v },
    { chave: 'servidor', get: () => this.servidorDropdownAberto, set: v => this.servidorDropdownAberto = v },
    { chave: 'anoLetivo', get: () => this.anoLetivoDropdownAberto, set: v => this.anoLetivoDropdownAberto = v },
    { chave: 'estabelecimento', get: () => this.estabelecimentoDropdownAberto, set: v => this.estabelecimentoDropdownAberto = v },
    { chave: 'turma', get: () => this.turmaDropdownAberta, set: v => this.turmaDropdownAberta = v }
  ];

  private toggleDropdown(chave: string): void {
    const alvo = this.dropdownFlags.find(d => d.chave === chave);
    if (!alvo) return;
    const novoValor = !alvo.get();
    alvo.set(novoValor);
    if (novoValor) {
      this.dropdownFlags.forEach(d => { if (d.chave !== chave) d.set(false); });
    }
  }

  toggleEntidadeDropdown(): void {
    this.toggleDropdown('entidade');
  }

  toggleServidorDropdown(): void {
    this.toggleDropdown('servidor');
  }

  toggleAnoLetivoDropdown(): void {
    this.toggleDropdown('anoLetivo');
  }

  toggleEstabelecimentoDropdown(): void {
    this.toggleDropdown('estabelecimento');
  }

  toggleTurmaDropdown(): void {
    this.toggleDropdown('turma');
  }

  selecionarEntidade(entidade: EntidadeOption): void {
    this.entidadeSelecionada = entidade.id;
    this.entidadeSelecionadaData = entidade;
    this.entidadeDropdownAberto = false;

    if (entidade.id !== null) {
      // Limpa hierarquia abaixo, exceto durante restauração
      if (!this.emProcessoDeRestauracao) {
        this.limparServidorEAbaixo();
      }
      // Detecta perfil (professor/secretário) replicando lógica do AngularJS
      this.detectarServidorProfessor(entidade);
    } else {
      this.entidadeSelecionadaData = null;
      this.limparServidorEAbaixo();
      this.camposHabilitados.servidor = false;
    }
  }

  selecionarServidor(servidor: ServidorOption): void {
    this.servidorSelecionado = servidor.id;
    this.servidorDropdownAberto = false;

    if (servidor.id !== null) {
      // Define perfil SECRETARIO se não for professor (replica AngularJS selecao-servidor.controller.js)
      if (!this.emProcessoDeRestauracao && !this.profileService.isProfileProfessor()) {
        this.profileService.setAsSecretario();
      }

      if (!this.emProcessoDeRestauracao) {
        this.limparAnoLetivoEAbaixo();
      }

      this.camposHabilitados.anoLetivo = true;
      this.carregarAnosLetivosFiltrados();
    } else {
      this.limparAnoLetivoEAbaixo();
      this.camposHabilitados.anoLetivo = false;
    }
  }

  selecionarAnoLetivo(ano: AnoLetivoOption): void {
    this.anoLetivoSelecionado = ano.id;
    this.anoLetivoDropdownAberto = false;

    if (ano.id !== null) {
      if (!this.emProcessoDeRestauracao) {
        this.limparEstabelecimentoEAbaixo();
      }
      this.camposHabilitados.estabelecimento = true;
      this.carregarEstabelecimentosFiltrados();
    } else {
      this.limparEstabelecimentoEAbaixo();
      this.camposHabilitados.estabelecimento = false;
    }
  }

  selecionarEstabelecimento(estabelecimento: EstabelecimentoOption): void {
    this.estabelecimentoSelecionado = estabelecimento.id;
    this.estabelecimentoDropdownAberto = false;

    if (estabelecimento.id !== null) {
      if (!this.emProcessoDeRestauracao) {
        this.limparTurmaEAbaixo();
      }
      this.camposHabilitados.turma = true;
      this.carregarTurmasFiltradas();
    } else {
      this.limparTurmaEAbaixo();
      this.camposHabilitados.turma = false;
    }
  }

  selecionarTurma(turma: TurmaOption): void {
    this.turmaSelecionada = turma.id;
    this.turmaDropdownAberta = false;

    if (turma.id !== null) {
      if (!this.emProcessoDeRestauracao) {
        this.limparComponentes();
      }
      this.camposHabilitados.componentes = true;
      this.carregarComponentes(turma.id);
    } else {
      this.limparComponentes();
      this.camposHabilitados.componentes = false;
    }
  }

  limparEntidadeField(): void {
    this.entidadeSelecionada = null;
    this.entidadeSelecionadaData = null;
    this.entidadeDropdownAberto = false;
    this.limparServidorEAbaixo();
  }

  limparServidorField(): void {
    this.servidorSelecionado = null;
    this.servidorDropdownAberto = false;
    this.limparAnoLetivoEAbaixo();
  }

  limparAnoLetivoField(): void {
    this.anoLetivoSelecionado = null;
    this.anoLetivoDropdownAberto = false;
    this.limparEstabelecimentoEAbaixo();
  }

  limparEstabelecimentoField(): void {
    this.estabelecimentoSelecionado = null;
    this.estabelecimentoDropdownAberto = false;
    this.limparTurmaEAbaixo();
  }

  limparTurmaField(): void {
    this.turmaSelecionada = null;
    this.turmaDropdownAberta = false;
    this.limparComponentes();
  }

  semOpcoesEntidades(): boolean {
    return !this.carregando && !this.loadingStates.entidades && this.entidades.length === 0;
  }

  /**
   * “Não há servidores” só quando não há o que escolher na API (ou sem entidades).
   * Se falta só escolher entidade, o campo usa “Selecione…” — não esta mensagem.
   */
  semOpcoesServidores(): boolean {
    if (this.servidorProfessorVinculado || this.detectandoServidorProfessor) {
      return false;
    }
    if (this.carregando || this.loadingStates.servidores) {
      return false;
    }
    if (this.semOpcoesEntidades()) {
      return true;
    }
    if (this.entidadeSelecionada === null) {
      return false;
    }
    return this.camposHabilitados.servidor && this.servidores.length === 0;
  }

  /**
   * “Não há ano letivo” quando a API não retorna anos ou quando um ancestral
   * realmente não tem opções — não quando falta só selecionar servidor/ano/etc.
   */
  semOpcoesAnosLetivos(): boolean {
    if (this.detectandoServidorProfessor) {
      return false;
    }
    if (this.carregando || this.loadingStates.anosLetivos) {
      return false;
    }
    if (this.semOpcoesEntidades()) {
      return true;
    }

    if (this.servidorProfessorVinculado) {
      if (this.loadingStates.servidores) {
        return false;
      }
      if (this.servidorSelecionado === null) {
        return true;
      }
    } else {
      if (this.entidadeSelecionada === null) {
        return false;
      }
      if (
        this.camposHabilitados.servidor &&
        !this.loadingStates.servidores &&
        this.servidores.length === 0
      ) {
        return true;
      }
      if (this.servidorSelecionado === null) {
        return false;
      }
    }

    if (!this.camposHabilitados.anoLetivo) {
      return false;
    }
    return this.anosLetivos.length === 0;
  }

  semOpcoesEstabelecimentos(): boolean {
    if (this.carregando || this.loadingStates.estabelecimentos) {
      return false;
    }
    if (this.semOpcoesAnosLetivos()) {
      return true;
    }
    if (!this.camposHabilitados.estabelecimento) {
      return false;
    }
    return this.estabelecimentos.length === 0;
  }

  semOpcoesTurmas(): boolean {
    if (this.carregando || this.loadingStates.turmas) {
      return false;
    }
    if (this.semOpcoesEstabelecimentos()) {
      return true;
    }
    if (!this.camposHabilitados.turma) {
      return false;
    }
    return this.turmas.length === 0;
  }

  semOpcoesComponentes(): boolean {
    if (this.carregando || this.loadingStates.componentes) {
      return false;
    }
    if (this.semOpcoesTurmas()) {
      return true;
    }
    if (this.turmaSelecionada === null) {
      return false;
    }
    return this.camposHabilitados.componentes && this.componentes.length === 0;
  }

  /** Exibe o bloco de componentes (lista, loading ou mensagem em cascata). */
  exibirBlocoComponentesCurriculares(): boolean {
    return (
      this.turmaSelecionada !== null ||
      this.loadingStates.componentes ||
      this.semOpcoesComponentes()
    );
  }

  getEntidadeSelecionadaTexto(): string {
    if (this.semOpcoesEntidades()) {
      return this.msgSemOpcoes.entidades;
    }
    if (!this.entidadeSelecionada) return 'Selecione...';
    return this.entidades.find(e => e.id === this.entidadeSelecionada)?.nome || 'Selecione...';
  }

  getServidorSelecionadoTexto(): string {
    if (this.servidorProfessorVinculado && this.servidorSelecionado) {
      return this.servidores.find(s => s.id === this.servidorSelecionado)?.nome || 'Selecione...';
    }
    if (this.semOpcoesServidores()) {
      return this.msgSemOpcoes.servidores;
    }
    if (!this.camposHabilitados.servidor && !this.servidorProfessorVinculado) {
      return 'Selecione...';
    }
    if (!this.servidorSelecionado) return 'Selecione...';
    return this.servidores.find(s => s.id === this.servidorSelecionado)?.nome || 'Selecione...';
  }

  getAnoLetivoSelecionadoTexto(): string {
    if (this.semOpcoesAnosLetivos()) {
      return this.msgSemOpcoes.anoLetivo;
    }
    if (!this.anoLetivoSelecionado) return 'Selecione...';
    const ano = this.anosLetivos.find(a => a.id === this.anoLetivoSelecionado);
    if (!ano) return 'Selecione...';
    return ano.descricao ? `${ano.ano} - ${ano.descricao}` : ano.ano.toString();
  }

  getEstabelecimentoSelecionadoTexto(): string {
    if (this.semOpcoesEstabelecimentos()) {
      return this.msgSemOpcoes.estabelecimentos;
    }
    if (!this.estabelecimentoSelecionado) return 'Selecione...';
    return this.estabelecimentos.find(e => e.id === this.estabelecimentoSelecionado)?.nome || 'Selecione...';
  }

  getTurmaSelecionadaTexto(): string {
    if (this.semOpcoesTurmas()) {
      return this.msgSemOpcoes.turmas;
    }
    if (!this.turmaSelecionada) return 'Selecione...';
    const turma = this.turmas.find(t => t.id === this.turmaSelecionada);
    return turma?.descricao || turma?.nome || 'Selecione...';
  }

  /**
   * Retorna o ano letivo (apenas o ano) para exibir na mensagem de "sem turmas"
   */
  getAnoLetivoTexto(): string {
    if (!this.anoLetivoSelecionado) return '';
    const ano = this.anosLetivos.find(a => a.id === this.anoLetivoSelecionado);
    return ano?.ano?.toString() || '';
  }

  /**
   * Métodos de filtro/busca para cada dropdown
   * Busca inteligente: ignora acentos, case-insensitive
   * Ordenação alfabética aplicada em todas as listas (exceto ano letivo, ver abaixo)
   */
  filtrarEntidades(): void {
    this.entidadesFiltradas = filtrarLista(
      this.entidades,
      this.entidadeSearchTerm,
      (e, termo) => normalizarTexto(e.nome).includes(termo),
      (a, b) => normalizarTexto(a.nome).localeCompare(normalizarTexto(b.nome))
    );
  }

  filtrarServidores(): void {
    this.servidoresFiltrados = filtrarLista(
      this.servidores,
      this.servidorSearchTerm,
      (s, termo) => normalizarTexto(s.nome).includes(termo),
      (a, b) => normalizarTexto(a.nome).localeCompare(normalizarTexto(b.nome))
    );
  }

  filtrarAnosLetivos(): void {
    // Ordenar por ano DESCENDENTE (mais recente primeiro) — diferente dos demais níveis
    this.anosLetivosFiltrados = filtrarLista(
      this.anosLetivos,
      this.anoLetivoSearchTerm,
      (a, termo) => normalizarTexto(a.ano.toString()).includes(termo) || normalizarTexto(a.descricao || '').includes(termo),
      (a, b) => Number(b.ano) - Number(a.ano)
    );
  }

  filtrarEstabelecimentos(): void {
    this.estabelecimentosFiltrados = filtrarLista(
      this.estabelecimentos,
      this.estabelecimentoSearchTerm,
      (e, termo) => normalizarTexto(e.nome).includes(termo) || normalizarTexto(e.codigo || '').includes(termo),
      (a, b) => normalizarTexto(a.nome).localeCompare(normalizarTexto(b.nome))
    );
  }

  filtrarTurmas(): void {
    this.turmasFiltradas = filtrarLista(
      this.turmas,
      this.turmaSearchTerm,
      (t, termo) => normalizarTexto(t.descricao || '').includes(termo) || normalizarTexto(t.nome || '').includes(termo),
      (a, b) => normalizarTexto(a.descricao).localeCompare(normalizarTexto(b.descricao))
    );
  }

  /**
   * Limpar termo de busca e resetar lista filtrada
   */
  limparBuscaEntidade(): void {
    this.entidadeSearchTerm = '';
    this.filtrarEntidades();
  }

  limparBuscaServidor(): void {
    this.servidorSearchTerm = '';
    this.carregarServidoresFiltrados();
  }

  limparBuscaAnoLetivo(): void {
    this.anoLetivoSearchTerm = '';
    this.filtrarAnosLetivos();
  }

  limparBuscaEstabelecimento(): void {
    this.estabelecimentoSearchTerm = '';
    this.filtrarEstabelecimentos();
  }

  limparBuscaTurma(): void {
    this.turmaSearchTerm = '';
    this.filtrarTurmas();
  }

  onServidorSearchChange(termo: string): void {
    if (this.servidorProfessorVinculado) {
      return;
    }
    this.servidorSearchSubject.next(termo);
  }

  private inicializarBuscaServidorDebounced(): void {
    this.servidorBuscaSubscription = this.servidorSearchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap((termo: string) => {
        const headersContextuais = this.construirHeadersContextuais();
        const params: any = {
          filter: termo,
          limit: 25,
          offset: 0,
          userAccessId: headersContextuais.userAccessId
        };
        this.loadingStates.servidores = true;
        return this.contextApiService.getServidores(params).pipe(
          catchError(() => {
            this.loadingStates.servidores = false;
            return of([]);
          })
        );
      })
    ).subscribe(servidores => {
      this.servidores = servidores.map((s: any) => ({ id: s.id, nome: s.pessoa.nome }));
      this.filtrarServidores();
      this.loadingStates.servidores = false;
    });
  }
}
